import type {
	Paginated,
	ParticipantContactBody,
	ParticipantContactResult,
	ParticipantEvent,
	ParticipantMedal,
	ParticipantProfile,
	ParticipantRegistration,
	ParticipantRegistrationBody,
	ParticipantTravelCard,
} from "@seagames/contracts";
import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";

/** A participant's own pages (`/me`, active role PARTICIPANT). */

export const meKeys = {
	all: ["me"] as const,
	profile: () => ["me", "profile"] as const,
	events: (page: number) => ["me", "events", page] as const,
	medals: () => ["me", "medals"] as const,
	registration: (acCardId: string) => ["me", "registration", acCardId] as const,
	travel: () => ["me", "travel"] as const,
};

export const meProfileQuery = queryOptions({
	queryKey: meKeys.profile(),
	queryFn: () => api.get<ParticipantProfile>("/me/profile"),
	staleTime: 60_000,
});

export const meEventsQuery = (page: number) =>
	queryOptions({
		queryKey: meKeys.events(page),
		queryFn: () => api.get<Paginated<ParticipantEvent>>(`/me/events?page=${page}`),
	});

export const meMedalsQuery = queryOptions({
	queryKey: meKeys.medals(),
	queryFn: () => api.get<{ items: ParticipantMedal[] }>("/me/medals"),
});

export const updateMyContact = (body: ParticipantContactBody) => api.patch<ParticipantContactResult>("/me/profile", body);

/** Finishing their own registration (`/me/registration/:acCardId`, DRAFT / RETURN only; another card → 404). */
export const meRegistrationQuery = (acCardId: string) =>
	queryOptions({
		queryKey: meKeys.registration(acCardId),
		queryFn: () => api.get<ParticipantRegistration>(`/me/registration/${acCardId}`),
		// The photos are presigned: a long-lived copy would show broken images.
		staleTime: 30_000,
	});

export const updateMyRegistration = (acCardId: string, body: ParticipantRegistrationBody) => api.patch<ParticipantRegistration>(`/me/registration/${acCardId}`, body);

/** The person makes both declarations on every submit (the button needs both ticked). */
export const submitMyRegistration = (acCardId: string, version: number) =>
	api.post<ParticipantRegistration>(`/me/registration/${acCardId}/submit`, { version, consent_truthfulness: true, consent_pdpa: true });

/** Their own arrival & departure, per live card (`/me/travel`, read-only). */
export const meTravelQuery = queryOptions({
	queryKey: meKeys.travel(),
	queryFn: () => api.get<{ items: ParticipantTravelCard[] }>("/me/travel"),
});
